// Pure helpers of the custom-vars editor (rows <-> map), shared by the task
// drawer and the "New task" form.

import {
  CUSTOM_VARS_MAX_KEYS,
  CUSTOM_VAR_KEY_RE,
  CUSTOM_VAR_STRING_MAX,
  type CustomVarValue,
  type CustomVars,
} from '@goblic/shared'

export type VarType = 'string' | 'number' | 'boolean'
export interface VarRow {
  key: string
  type: VarType
  value: string
}

export function typeOf(value: CustomVarValue): VarType {
  return typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'boolean' : 'string'
}

export function toRows(vars: CustomVars): VarRow[] {
  return Object.entries(vars).map(([key, value]) => ({
    key,
    type: typeOf(value),
    value: String(value),
  }))
}

/** The edited rows as a map, or the first thing wrong with them. */
export function fromRows(rows: VarRow[]): { vars: CustomVars } | { error: string } {
  const vars: CustomVars = {}
  for (const row of rows) {
    const key = row.key.trim()
    if (!key) return { error: 'Every var needs a key.' }
    if (!CUSTOM_VAR_KEY_RE.test(key))
      return {
        error: `“${key}” isn’t a valid key: start with a letter, then letters, digits, _ . - (max 64).`,
      }
    if (key in vars) return { error: `The key “${key}” is used twice.` }
    if (row.type === 'number') {
      const n = Number(row.value)
      if (row.value.trim() === '' || !Number.isFinite(n))
        return { error: `“${key}” needs a number.` }
      vars[key] = n
    } else if (row.type === 'boolean') {
      vars[key] = row.value === 'true'
    } else {
      if (row.value.length > CUSTOM_VAR_STRING_MAX)
        return { error: `“${key}” is longer than ${CUSTOM_VAR_STRING_MAX} characters.` }
      vars[key] = row.value
    }
  }
  if (Object.keys(vars).length > CUSTOM_VARS_MAX_KEYS)
    return { error: `At most ${CUSTOM_VARS_MAX_KEYS} custom vars.` }
  return { vars }
}

/** A row nobody typed anything in (an "Add var" that was left empty). */
const isBlankRow = (row: VarRow): boolean => row.key.trim() === '' && row.value.trim() === ''

/** The "New task" form's rows as a map: untouched blank rows are skipped, the rest is validated like `fromRows`. */
export function formRowsToVars(rows: VarRow[]): { vars: CustomVars } | { error: string } {
  return fromRows(rows.filter((row) => !isBlankRow(row)))
}
